/**
 * RBAC Type Definitions
 * Comprehensive types for Role-Based Access Control system
 */

// Core role types
export type UserRole = 'admin' | 'owner' | 'customer' | 'viewer';

// Permission string format: resource:action (e.g., 'users:create', 'payments:manage')
export type Permission = string;

// Role hierarchy (lower number = higher priority)
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  admin: 1,
  owner: 2,
  customer: 3,
  viewer: 4,
} as const;

// Role definition structure
export interface RoleDefinition {
  id: UserRole;
  name: string;
  description: string;
  permissions: Permission[];
  restrictions: {
    directoryVisible: boolean;
    profileFieldsRequired: boolean;
    canAccessAdmin: boolean;
    canInviteUsers: boolean;
    canViewAnalytics: boolean;
    canManagePayments: boolean;
    canExportData: boolean;
    canDeleteUsers: boolean;
    canConfigureWebhooks: boolean;
    canManageSecrets: boolean;
  };
  hierarchy: number;
}

// Permission categories for organization
export interface PermissionCategory {
  id: string;
  name: string;
  description: string;
  permissions: Permission[];
}

// RBAC context for permission checking
export interface RBACContext {
  projectId?: string;
  user?: any;
  route?: string;
  customData?: Record<string, any>;
}

// Role check result
export interface RoleCheckResult {
  hasAccess: boolean;
  role?: UserRole;
  reason?: string;
  fallbackRoute?: string;
}

// Permission check result
export interface PermissionCheckResult {
  hasPermission: boolean;
  permissions?: Permission[];
  missingPermissions?: Permission[];
  reason?: string;
}

// User role state
export interface UserRoleState {
  currentRole: UserRole | null;
  projectId: string | null;
  isLoading: boolean;
  lastUpdated: Date | null;
  customClaims: Record<string, any> | null;
}

// RBAC configuration
export interface RBACConfig {
  enabled: boolean;
  defaultRole: UserRole;
  enableDebug: boolean;
  enforceHierarchy: boolean;
  allowRoleEscalation: boolean;
  tokenRefreshOnLoad: boolean;
}

// Route protection options
export interface RouteProtectionOptions {
  requiredRole?: UserRole;
  requiredPermissions?: Permission[];
  allowedRoles?: UserRole[];
  mode?: 'strict' | 'permissive';
  fallbackRoute?: string;
}

// Custom claims formats we support
export interface CustomClaimsFormats {
  // Direct project mapping: { "projectId": "role" }
  direct: Record<string, string>;
  
  // Explicit role mapping: { "projectId:role": "role" }
  explicit: Record<string, string>;
  
  // Project roles object: { "projectRoles": { "projectId": "role" } }
  nested: {
    projectRoles?: Record<string, string>;
    roles?: Record<string, string>;
  };
  
  // Legacy Shirly admin
  shirly?: string;
  demo?: string;
}

// Utility types for role comparison
export type RoleComparison = 'higher' | 'equal' | 'lower';

// Export types for router integration
export interface RBACRouteMeta {
  requiredRole?: UserRole;
  requiredPermissions?: Permission[];
  allowedRoles?: UserRole[];
  rbacMode?: 'strict' | 'permissive';
  skipRBAC?: boolean;
}

// Event types for role changes
export interface RoleChangeEvent {
  oldRole: UserRole | null;
  newRole: UserRole | null;
  projectId: string | null;
  timestamp: Date;
  trigger: 'login' | 'project-change' | 'claims-refresh' | 'manual';
} 